import {
  DOCUMENT,
  inject,
  Injectable,
  Renderer2,
  RendererFactory2,
} from '@angular/core';
import { DataService } from '@core/services/data.service';
import { IframeTools } from '@core/services/iframe-tools.service';
import { environment } from '@environments/environment';
import { ENVIRONMENTS } from '@shared/constants';
import { LoyaltyChallengeConfig } from '@shared/models';

@Injectable({
  providedIn: 'root',
})
export class ScriptService {
  private readonly document: Document = inject(DOCUMENT);
  private readonly iframeTools: IframeTools = inject(IframeTools);
  private readonly dataService: DataService = inject(DataService);

  private readonly renderer: Renderer2 = inject(
    RendererFactory2
  ).createRenderer(null, null);

  private loadedScripts = new Map<string, Promise<void>>();

  /**
   * Load External Scripts (e.g. chatbot)
   */
  load(): Promise<void> {
    const config = this.dataService.config() as LoyaltyChallengeConfig;
    const { scripts } = config;

    if (
      environment.env === ENVIRONMENTS.PLAYWRIGHT ||
      !scripts ||
      !scripts.length
    ) {
      return Promise.resolve();
    }

    for (const scriptEl of scripts) {
      // Skip loading scripts in the main window if we are in an iframe
      if (!scriptEl.loadInIframe && this.iframeTools.isInIframe) {
        return Promise.resolve();
      }

      const key = scriptEl.id;

      // Already loading/loaded in this session
      const existing = this.loadedScripts.get(key);
      if (existing) return existing;

      // Already present in DOM (e.g., previous navigation)
      if (this.document.getElementById(key)) {
        const already = Promise.resolve();
        this.loadedScripts.set(key, already);
        return already;
      }

      const p = new Promise<void>((resolve, reject) => {
        const script: HTMLScriptElement = this.renderer.createElement('script');
        script.type = 'text/javascript';
        script.src = scriptEl.src;
        script.async = true;
        script.id = scriptEl.id;

        script.onload = () => resolve();
        script.onerror = () =>
          reject(new Error(`Failed to load ${scriptEl.src}`));

        this.renderer.appendChild(this.document.body, script);
      });

      this.loadedScripts.set(key, p);
      return p;
    }

    return Promise.resolve();
  }
}
